import { useEffect, useRef } from 'react'

/**
 * Mirrors a React value into `ref.current` after each render (via `useEffect`).
 *
 * **Why:** Some code needs a **stable ref object** (same identity every render) while always reading the
 * **latest** committed value—e.g. `getLatestAttachmentsForExtraction` after uploads settle, without
 * threading that value through every callback dependency list.
 *
 * **If we skipped this:** Either duplicate `useRef` + `useEffect` at each call site, or async/sync
 * helpers close over **stale** state (wrong attachment snapshot), or you add `value` to many `useCallback`
 * deps and churn handlers unnecessarily.
 */
export const useSyncRef = <T>(value: T) => {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref
}
